import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import moment from 'moment';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import Header from '../../../components/Layouts/MyPage/Header/MyPageHeader.index';
import Footer from '../../../components/Layouts/MyPage/Footer/MyPageFooter.index';
import './style/ReservationHistoryDetail.scss';

import ReservationCancelModal from './Modal/ReservationCancel/ReservationCancel';

import { RootState } from '../../../store/reducers';
import {
  RESERVE_HISTORY_DETAIL_STATE_INIT,
  GET_RESERVE_HISTORY_DETAIL_INITIAL,
  GET_RESERVE_HISTORY_DETAIL_REQUEST,
  PUT_CANCEL_RESERVE_INITIAL,
  PUT_CANCEL_RESERVE_REQUEST,
} from '../../../store/actions';
import { ReserveHistoryDetailStateType } from '../../../store/reducers/reserveHistory/state/reserveHistoryDetailStateReducer';
import {
  ReserveDataType,
  GetReserveHistoryDetailStateType,
} from '../../../store/reducers/reserveHistory/getReserveHistoryDetailReducer';
import { PutCancelReserveStateType } from '../../../store/reducers/reserveHistory/putCancelReserveReducer';
import googleCalendarIcon from './style/img/google-calendar.svg';

dayjs.extend(utc);
dayjs.extend(timezone);

const ReservationHistoryDetail: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const reserveHistoryDetailState = useSelector<RootState, ReserveHistoryDetailStateType>(
    (state) => state.reserveHistory.reserveHistoryDetailState,
  );

  const { isGetReserveHistoryDetailLoaded, getReserveHistoryDetail } = useSelector<
    RootState,
    GetReserveHistoryDetailStateType
  >((state) => state.reserveHistory.getReserveHistoryDetail);

  const {
    isPutCancelReserveLoading,
    isPutCancelReserveLoaded,
    errMessage: cancelReserveErrMessage,
  } = useSelector<RootState, PutCancelReserveStateType>((state) => state.reserveHistory.putCancelReserve);

  const [modalCancelReserveShow, setModalCancelReserveShow] = useState<boolean>(false);

  useEffect(() => {
    return () => {
      dispatch({
        type: RESERVE_HISTORY_DETAIL_STATE_INIT,
      });
      dispatch({
        type: GET_RESERVE_HISTORY_DETAIL_INITIAL,
      });
      dispatch({
        type: PUT_CANCEL_RESERVE_INITIAL,
      });
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('simpleAuthResult_birthday') && sessionStorage.getItem('simpleAuthResult_telephone')) {
      if (reserveHistoryDetailState.transitionTarget) {
        window.scrollTo(0, 0);

        dispatch({
          type: GET_RESERVE_HISTORY_DETAIL_REQUEST,
          visitorId: sessionStorage.getItem('simpleAuthResult_visitorId') || null,
          birthday: sessionStorage.getItem('simpleAuthResult_birthday'),
          telephone: sessionStorage.getItem('simpleAuthResult_telephone'),
          historyId: reserveHistoryDetailState.historyId,
          history,
        });
      } else {
        history.push('/history');
      }
    } else {
      const url = '/';
      history.push(url);
    }
  }, [history, dispatch, reserveHistoryDetailState.transitionTarget]);

  // modal-cancel-reserve
  const onClickCancelReserve = () => {
    setModalCancelReserveShow(true);
  };

  useEffect(() => {
    if (!modalCancelReserveShow) {
      dispatch({
        type: PUT_CANCEL_RESERVE_INITIAL,
      });
    }
  }, [modalCancelReserveShow]);

  const handleCancelReserve = () => {
    dispatch({
      type: PUT_CANCEL_RESERVE_REQUEST,
      contentsId: getReserveHistoryDetail.data.contents_id,
      scheduleId: getReserveHistoryDetail.data.schedule_id,
      history,
    });
  };

  const onHideCancelReserveModal = () => {
    setModalCancelReserveShow(false);
  };

  useEffect(() => {
    if (isPutCancelReserveLoaded) {
      sessionStorage.removeItem('simpleAuthResult_visitorId');
      sessionStorage.removeItem('simpleAuthResult_birthday');
      sessionStorage.removeItem('simpleAuthResult_telephone');
      history.push('/history/detail/cancel-complete');
    }
  }, [isPutCancelReserveLoaded]);

  const buildGoogleCalendarUrl = (params: { text: string; dates: string; location: string; details: string }) => {
    return (
      'https://calendar.google.com/calendar/render?action=TEMPLATE' +
      `&text=${encodeURIComponent(params.text)}` +
      `&dates=${encodeURIComponent(params.dates)}` +
      `&ctz=${encodeURIComponent('Asia/Tokyo')}` +
      `&location=${encodeURIComponent(params.location)}` +
      `&details=${encodeURIComponent(params.details)}`
    );
  };

  const onClickGoogleCalendarAdd = (item: ReserveDataType) => () => {
    if (item.schedule_id && item.contents_id) {
      const simpleAuthUrl = `${window.location.origin}/simpleAuth`;
      const scheduleDate = dayjs(item.schedule_date).tz('Asia/Tokyo').format('YYYYMMDD');
      const endDate = item.end_time === '0000' ? dayjs(scheduleDate).add(1, 'day').format('YYYYMMDD') : scheduleDate;
      const dates = `${scheduleDate}T${item.start_time}00/${endDate}T${item.end_time}00`;
      const location = `${item.club_web_show_name}, ${item.address}`;
      const details = `${item.contents_web_show_name}\nスペース\u3000${item.alternate_label}\n\n<a href="${simpleAuthUrl}">予約の詳細</a>`;
      window.open(
        buildGoogleCalendarUrl({
          text: item.contents_web_show_name ?? '',
          dates,
          location,
          details,
        }),
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  const onClickReserveHistory = () => {
    history.push('/history');
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />

        <div className="reservation-history-detail" style={{ flex: '1' }}>
          <main className="main-layout l-main">
            <section className="p-title-area">
              <p className="c-title">ご予約内容</p>
            </section>

            {isGetReserveHistoryDetailLoaded ? (
              <>
                <section className="p-content-area u-gap50 u-pb0">
                  <div className="p-content-area__column p-content-area__column--width800 u-gap30 u-sp-gap20">
                    {/* <!-- 予約内容 --> */}
                    {getReserveHistoryDetail.data && getReserveHistoryDetail.data.data_id !== '' ? (
                      <>
                        <div className="p-reservation-card u-sp-gap10">
                          <div className="p-reservation-card__header">
                            <p className="p-reservation-card__row-text">
                              {getReserveHistoryDetail.data.club_web_show_name}
                              {getReserveHistoryDetail.data.contents_annex_flag === 1 ? (
                                <>
                                  <span className="c-genre-label">別館</span>
                                </>
                              ) : (
                                <></>
                              )}
                            </p>
                            <div className="p-reservation-card__title">
                              {getReserveHistoryDetail.data.contents_web_show_name}
                            </div>
                          </div>

                          <div className="p-reservation-card__details">
                            <dl className="p-detail-list">
                              <div className="p-detail-list__item">
                                <dt className="p-detail-list__label">実施日時</dt>
                                <dd className="p-detail-list__value">
                                  {getReserveHistoryDetail.data.reserve_time_label}
                                </dd>
                              </div>

                              {/* <!-- アコーディオン --> */}
                              <div className="p-detail-list__accordion-box">
                                <div className="p-detail-list__accordion-label">店舗名</div>
                                <div className="p-detail-list__accordion-value">
                                  {getReserveHistoryDetail.data.club_web_show_name}
                                  {getReserveHistoryDetail.data.club_equipment_annex_flag === 1 ? (
                                    <>
                                      <span className="c-genre-label c-genre-label--large">別館</span>
                                    </>
                                  ) : (
                                    <></>
                                  )}
                                </div>

                                {/* <!-- アコーディオンボタン --> */}
                                <details className="c-accordion p-detail-list__store-accordion">
                                  <summary className="p-detail-list__link-title">店舗詳細を確認</summary>
                                </details>

                                {/* <!-- アコーディオンコンテンツ --> */}
                                <div className="p-detail-list__link-content">
                                  <dl className="p-detail-list__inner">
                                    <div className="p-detail-list__item">
                                      <dt className="p-detail-list__label">営業時間</dt>
                                      <dd className="p-detail-list__value">
                                        {getReserveHistoryDetail.data.open_date
                                          ?.split(/[\r\n]+/)
                                          .map((s) => s.trim())
                                          .filter(Boolean)
                                          .map((item, index) => {
                                            return (
                                              <span key={item}>
                                                {item}
                                                <br />
                                              </span>
                                            );
                                          })}
                                      </dd>
                                    </div>
                                    <div className="p-detail-list__item">
                                      <dt className="p-detail-list__label">電話番号</dt>
                                      <dd className="p-detail-list__value">
                                        {getReserveHistoryDetail.data.telephone_number ? (
                                          <>
                                            <a
                                              href={`tel:${getReserveHistoryDetail.data.telephone_number.replaceAll(
                                                '-',
                                                '',
                                              )}`}
                                              className="p-detail-list__tel"
                                            >
                                              {getReserveHistoryDetail.data.telephone_number}
                                            </a>
                                          </>
                                        ) : (
                                          <></>
                                        )}
                                      </dd>
                                    </div>
                                    <div className="p-detail-list__item">
                                      <dt className="p-detail-list__label">住所</dt>
                                      <dd className="p-detail-list__value">
                                        {getReserveHistoryDetail.data.post_id
                                          ? `〒${getReserveHistoryDetail.data.post_id}`
                                          : ''}
                                        {getReserveHistoryDetail.data.post_id &&
                                        (getReserveHistoryDetail.data.address ||
                                          getReserveHistoryDetail.data.addr_buildname)
                                          ? ' '
                                          : ''}
                                        {getReserveHistoryDetail.data.address
                                          ? getReserveHistoryDetail.data.address
                                          : ''}
                                        {getReserveHistoryDetail.data.addr_buildname
                                          ? getReserveHistoryDetail.data.addr_buildname
                                          : ''}
                                      </dd>
                                    </div>
                                    {getReserveHistoryDetail.data.contents_annex_flag === 1 ? (
                                      <>
                                        <div className="p-detail-list__item">
                                          <dt className="p-detail-list__label">別館住所</dt>
                                          <dd className="p-detail-list__value">
                                            {getReserveHistoryDetail.data.annex_post_id
                                              ? `〒${getReserveHistoryDetail.data.annex_post_id}`
                                              : ''}
                                            {getReserveHistoryDetail.data.annex_post_id &&
                                            (getReserveHistoryDetail.data.annex_address ||
                                              getReserveHistoryDetail.data.annex_addr_buildname)
                                              ? ' '
                                              : ''}
                                            {getReserveHistoryDetail.data.annex_address
                                              ? getReserveHistoryDetail.data.annex_address
                                              : ''}
                                            {getReserveHistoryDetail.data.annex_addr_buildname
                                              ? getReserveHistoryDetail.data.annex_addr_buildname
                                              : ''}
                                          </dd>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                    {getReserveHistoryDetail.data.contents_classification === 0 &&
                                    getReserveHistoryDetail.data.layout_picture ? (
                                      <>
                                        <div className="p-detail-list__item p-detail-list__item--flex-start">
                                          <dt className="p-detail-list__label">配置図</dt>
                                          <dd className="p-detail-list__value p-detail-list__value--width-full">
                                            <picture className="p-detail-list__picture">
                                              <img
                                                loading="lazy"
                                                className="p-detail-list__image"
                                                src={getReserveHistoryDetail.data.layout_picture}
                                                alt=""
                                              />
                                            </picture>
                                          </dd>
                                        </div>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </dl>
                                </div>
                              </div>

                              <div className="p-detail-list__item">
                                <dt className="p-detail-list__label">スペース</dt>
                                <dd className="p-detail-list__value">
                                  {/* A・レンタルクラブ設置席 */}
                                  {getReserveHistoryDetail.data.alternate_label
                                    ? getReserveHistoryDetail.data.alternate_label
                                    : ''}
                                  {getReserveHistoryDetail.data.alternate_label &&
                                  getReserveHistoryDetail.data.installed_seats_name
                                    ? '・'
                                    : ''}
                                  {getReserveHistoryDetail.data.installed_seats_name
                                    ? getReserveHistoryDetail.data.installed_seats_name
                                    : ''}
                                </dd>
                              </div>

                              {getReserveHistoryDetail.data.follow_people_upper != null &&
                              Number(getReserveHistoryDetail.data.follow_people_upper) > 0 ? (
                                <>
                                  <div className="p-detail-list__item">
                                    <dt className="p-detail-list__label">同行者</dt>
                                    <dd className="p-detail-list__value">
                                      {getReserveHistoryDetail.data.companion
                                        ? getReserveHistoryDetail.data.companion
                                        : 0}
                                      名
                                    </dd>
                                  </div>
                                </>
                              ) : (
                                <></>
                              )}
                            </dl>
                          </div>

                          {!!true ||
                          (getReserveHistoryDetail.data.reserve_time &&
                            moment().isBefore(moment(getReserveHistoryDetail.data.reserve_time))) ? (
                            <>
                              <div className="p-reservation-card__footer">
                                <div className="p-reservation-card__cancel-info">
                                  <button
                                    type="button"
                                    className="p-reservation-card__cancel-link c-link"
                                    onClick={onClickCancelReserve}
                                    disabled={
                                      false &&
                                      !(
                                        getReserveHistoryDetail.data.cancel_available_time &&
                                        moment().isBefore(moment(getReserveHistoryDetail.data.cancel_available_time))
                                      )
                                    }
                                  >
                                    予約をキャンセルする
                                  </button>
                                  <p className="p-reservation-card__cancel-deadline">
                                    {`${getReserveHistoryDetail.data.cancel_available_time_label} までキャンセル可能`}
                                  </p>
                                </div>
                                <div className="p-reservation-card__google-calendar">
                                  <button
                                    type="button"
                                    className="p-reservation-card__cancel-link c-link"
                                    onClick={onClickGoogleCalendarAdd(getReserveHistoryDetail.data)}
                                  >
                                    <img
                                      src={googleCalendarIcon}
                                      alt="Googleカレンダーに追加する"
                                      className="p-reservation-card__google-calendar-icon"
                                    />
                                    <span>Googleカレンダーに追加する</span>
                                  </button>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="p-reservation-card__footer no-data" />
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-reservation-card u-sp-gap10">
                          <div className="p-reservation-card__footer">
                            <p className="p-reservation-card__row-text">ご予約情報存在しません。</p>
                          </div>
                        </div>
                      </>
                    )}

                    {/* <!-- 予約者情報 --> */}
                    {getReserveHistoryDetail.visitor_data ? (
                      <>
                        <div className="p-info-card">
                          <div className="p-info-card__title">ご予約者情報</div>
                          <div className="p-info-card__list">
                            {/* <!-- テキスト --> */}
                            <div className="p-form-confirm reservator-info-form-item">
                              <span className="p-form-confirm__label p-form-confirm__label--width145 reservator-info-form-item-label">
                                お名前
                              </span>
                              <div className="p-form-confirm__text reservator-info-form-item-text">
                                {getReserveHistoryDetail.visitor_data.visitor_name}
                              </div>
                            </div>

                            {/* <!-- テキスト --> */}
                            <div className="p-form-confirm reservator-info-form-item">
                              <span className="p-form-confirm__label p-form-confirm__label--width145 reservator-info-form-item-label">
                                ふりがな
                              </span>
                              <div className="p-form-confirm__text reservator-info-form-item-text">
                                {getReserveHistoryDetail.visitor_data.visitor_sei}
                                {getReserveHistoryDetail.visitor_data.visitor_sei &&
                                getReserveHistoryDetail.visitor_data.visitor_mei
                                  ? ' '
                                  : ''}
                                {getReserveHistoryDetail.visitor_data.visitor_mei}
                              </div>
                            </div>

                            {/* <!-- テキスト --> */}
                            <div className="p-form-confirm reservator-info-form-item">
                              <span className="p-form-confirm__label p-form-confirm__label--width145 reservator-info-form-item-label">
                                連絡先電話番号
                              </span>
                              <div className="p-form-confirm__text reservator-info-form-item-text">
                                {getReserveHistoryDetail.visitor_data.contact_telephone}
                              </div>
                            </div>

                            {/* <!-- テキスト --> */}
                            <div className="p-form-confirm reservator-info-form-item">
                              <span className="p-form-confirm__label p-form-confirm__label--width145 reservator-info-form-item-label">
                                メールアドレス
                              </span>
                              <div className="p-form-confirm__text reservator-info-form-item-text">
                                {getReserveHistoryDetail.visitor_data.mail_address}
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <></>
                    )}
                  </div>
                </section>
              </>
            ) : (
              <></>
            )}

            <section className="p-button-area">
              {/* <!-- ボタン --> */}
              <button
                type="button"
                className="c-button c-button--secondary c-button--large"
                onClick={onClickReserveHistory}
              >
                予約一覧に戻る
              </button>
            </section>
          </main>
        </div>

        <ReservationCancelModal
          show={modalCancelReserveShow}
          isDisabled={isPutCancelReserveLoading}
          handle={handleCancelReserve}
          err={cancelReserveErrMessage}
          onHide={onHideCancelReserveModal}
        />

        <Footer />
      </div>
    </>
  );
};

export default ReservationHistoryDetail;
